<template>
    <v-layout row column data-testid="metadata-form" id="metadata-form">
        <v-flex v-for="(meta) in metadata" :key="meta.metadata">
            <component
                v-bind:is="getMetaDataComponent(meta)"
                v-if="meta.status === 1 && getComponentDependency(meta)"
                :metadata="getBaseMetadataById(meta.metadata)"
                :other-metadata="metadata"
                :value="meta.value"
                outline
                with-icon
                :placeholder="getPlaceholderFromMeta(meta)"
                :required="meta.required"
                :is-override="meta.isOverride"
                :previous-appointment-client-data="previousAppointmentClientData"
                :service-id="serviceId"
                @update:value="updateMetadataValue(meta, $event)"
                @updateOtherMetadata="updateOtherMetadata"
                @updateProfileMetadata="updateProfileMetadata"
                @updatePatientUuid="updatePatientUuid"
            />
        </v-flex>
    </v-layout>
</template>

<script>
import { mapGetters } from 'vuex';
import { upperFirst, camelCase, isNull, isNil, constant } from 'lodash';
import { MetadataEnum } from '@/enums';
import { maskNam, maskPhoneNumber } from '@/utils';

export default {
    name: 'metadata-form',
    props: {
        metadata: { type: Array, required: true },
        previousAppointmentClientData: { type: Object, required: true },
        serviceId: { type: Number, required: false },
    },
    components: {
        'NamExpirationSequential': () => constant(null),
        'AccompanyingPerson': () => import('@/components/metadata/inputs/AccompanyingPerson'),
        'Address': () => import('@/components/metadata/inputs/trimoz-address/TrimozAddress'),
        'Ah635AllergicReactionsQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'Ah635BleedingDisorderQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'Ah635HealthProblemsQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'Ah635ImmunizationQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'Ah635ImmunosuppressionQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'Ah635PregnancyQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'Birthday': () => import('@/components/metadata/inputs/Birthday/Birthday'),
        'BirthPremature': () => import('@/components/metadata/inputs/BirthPremature'),
        'BirthPrematureWeekCount': () => import('@/components/metadata/inputs/BirthPrematureWeekCount'),
        'Cellphone': () => import('@/components/metadata/inputs/cellphone/Cellphone.vue'),
        'Comments': () => import('@/components/metadata/inputs/comments/Comments'),
        'CommPhoneOnly': () => import('@/components/metadata/inputs/checkbox/MetadataCheckbox.vue'),
        'ConsentFormConfirmationCode': () => import('@/components/metadata/inputs/ConsentFormConfirmationCode'),
        'CoveredRamq': () => import('@/components/metadata/inputs/CoveredRamq'),
        'Covid511ConfirmReadDocument': () => import('@/components/metadata/inputs/Covid511ConfirmReadDocument'),
        'Covid511ConfirmIAmParent': () => import('@/components/metadata/inputs/checkbox/MetadataCheckbox.vue'),
        'Covid511ConfirmConsentToVaccine': () => import('@/components/metadata/inputs/Covid511ConfirmConsentToVaccine'),
        'DoctorName': () => import('@/components/metadata/inputs/doctor-name/DoctorName'),
        'DrugName': () => import('@/components/metadata/inputs/drug-name/DrugName'),
        'Email': () => import('@/components/metadata/inputs/email/Email'),
        'EmployeeEstablishment': () => import('@/components/metadata/inputs/EmployeeEstablishment'),
        'EmployeeCategory': () => import('@/components/metadata/inputs/employee-category/EmployeeCategory'),
        'EmployeeNumber': () => import('@/components/metadata/inputs/employee-number/EmployeeNumber'),
        'EmployerName': () => import('@/components/metadata/inputs/employer-name/EmployerName'),
        'FatherFirstName': () => import('@/components/metadata/inputs/father-first-name/FatherFirstName'),
        'FatherLastName': () => import('@/components/metadata/inputs/father-last-name/FatherLastName'),
        'FirstName': () => import('@/components/metadata/inputs/FirstName/FirstName'),
        'Gender': () => import('@/components/metadata/inputs/Gender'),
        'GeolocShortPostcode': () => import('@/components/metadata/inputs/geo-loc-short-postcode/GeolocShortPostcode.vue'),
        'InfluenzaHealthStatus': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'InfluenzaIsImmunosuppressive': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'InfluenzaIsPregnant': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'InfluenzaReceivedVaccineLastMonth': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'InfluenzaHasImmunocompromisedContact': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'InfluenzaPeopleWhoLiveWithMe': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'InfluenzaHasBloodTransfusion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'InfluenzaReceivedPneumococcalVaccine': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'InfluenzaIsHealthCareWorker': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'InfluenzaReceivedShinglesVaccine': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'InfluenzaHasSevereReaction': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'JobTitle': () => import('@/components/metadata/inputs/job-title/JobTitle'),
        'LastName': () => import('@/components/metadata/inputs/LastName/LastName'),
        'MotherFirstName': () => import('@/components/metadata/inputs/mother-first-name/MotherFirstName'),
        'MotherLastName': () => import('@/components/metadata/inputs/mother-last-name/MotherLastName'),
        'Nam': () => import('@/components/metadata/inputs/Nam/Nam'),
        'Occupation': () => import('@/components/metadata/inputs/occupation/Occupation'),
        'PCode': () => import('@/components/metadata/inputs/p-code/PCode'),
        'Phone': () => import('@/components/metadata/inputs/phone/Phone'),
        'PostalCode': () => import('@/components/metadata/inputs/postal-code/PostalCode'),
        'PregnancyDpa': () => import('@/components/metadata/inputs/PregnancyDpa'),
        'PriorityGroup': () => import('@/components/metadata/inputs/priority-group/PriorityGroup'),
        'ProfileNumber': () => import('@/components/metadata/inputs/profile-number/ProfileNumber'),
        'RequesterLink': () => import('@/components/metadata/inputs/RequesterLink/RequesterLink'),
        'RequesterName': () => import('@/components/metadata/inputs/requester-name/RequesterName'),
        'ResidentFirstNameInitial': () => import('@/components/metadata/inputs/resident-first-name-initial/ResidentFirstNameInitial'),
        'ResidentLastName': () => import('@/components/metadata/inputs/resident-last-name/ResidentLastName'),
        'ResidentRoomNumber': () => import('@/components/metadata/inputs/resident-room-number/ResidentRoomNumber'),
        'ResidentBirthdayMonth': () => import('@/components/metadata/inputs/resident-birthday-month/ResidentBirthdayMonth'),
        'ResidentContactAllowZoom': () => import('@/components/metadata/inputs/checkbox/MetadataCheckbox.vue'),
        'ResidentContactAlternative': () => import('@/components/metadata/inputs/resident-contact-alternative/ResidentContactAlternative'),
        'ResidentMultipleUsers': () => import('@/components/metadata/inputs/checkbox/MetadataCheckbox.vue'),
        'ScannedRequest': () => import('@/components/metadata/inputs/trimoz-upload-file/TrimozUploadFile.vue'),
        'SchoolClass': () => import('@/components/metadata/inputs/school-class/SchoolClass'),
        'SchoolName': () => import('@/components/metadata/inputs/school-name/SchoolName.vue'),
        'TravelTravelerCount': () => import('@/components/metadata/inputs/travel/TravelTravelerCount'),
        'TravelTravelersInfo': () => import('@/components/metadata/inputs/travel/TravelTravelersInfo'),
        'TravelTravelersInfoV2': () => import('@/components/metadata/inputs/travel/TravelTravelersInfoV2'),
        'TravelDestinationCountry': () => import('@/components/metadata/inputs/travel/TravelDestinationCountry'),
        'TravelDepartureDate': () => import('@/components/metadata/inputs/travel/TravelDepartureDate'),
        'TutorFirstName': () => import('@/components/metadata/inputs/tutor-first-name/TutorFirstName'),
        'TutorLastName': () => import('@/components/metadata/inputs/tutor-last-name/TutorLastName'),
        'TutorOccupation': () => import('@/components/metadata/inputs/tutor-occupation/TutorOccupation'),
        'UnknownFather': () => import('@/components/metadata/inputs/UnknownFather'),
        'UnknownMother': () => import('@/components/metadata/inputs/UnknownMother'),
        'VaccineApprehension': () => import('@/components/metadata/inputs/VaccineApprehension'),
        'VaccineSecurityAcceptance': () => import('@/components/metadata/inputs/VaccineSecurityAcceptance'),
        'VirtualAccount': () => import('@/components/metadata/inputs/virtual-account/VirtualAccount'),
        'WorkLocation': () => import('@/components/metadata/inputs/work-location/WorkLocation'),
        'ScreeningFeesAcknowledged': () => import('@/components/metadata/inputs/screening-fees-acknowledged/ScreeningFeesAcknowledged'),
        'CovidScreeningEmailConsent': () => import('@/components/metadata/inputs/covid-screening-email-consent/CovidScreeningEmailConsent'),
        'CovidScreeningSecurityConsent': () => import('@/components/metadata/inputs/covid-screening-security-consent/CovidScreeningSecurityConsent'),
        'PhxCovidScreeningReason': () => import('@/components/metadata/inputs/covid-screening-reason/PhxCovidScreeningReason'),
        'PhxCovidExperiencingSymptoms': () => import('@/components/metadata/inputs/covid-experiencing-symptoms/PhxCovidExperiencingSymptoms'),
        'PhxCovidTravelledAndAdvisedToQuarantine': () => import('@/components/metadata/inputs/covid-travelled-and-advised-to-quarantine/PhxCovidTravelledAndAdvisedToQuarantine'),
        'PhxCovidReceivedPositivePcrTest': () => import('@/components/metadata/inputs/covid-received-positive-pcr-test/PhxCovidReceivedPositivePcrTest'),
        'PhxCovidReceivedPositiveExposureNotification': () => import('@/components/metadata/inputs/covid-received-positive-covid-exposure-notification/PhxCovidReceivedPositiveExposureNotification'),
        'PhxCovidAskedToGetAScreeningTest': () => import('@/components/metadata/inputs/covid-asked-to-get-a-screening-test/PhxCovidAskedToGetAScreeningTest'),
        'PhxCovidScreeningConsent': () => import('@/components/metadata/inputs/covid-screening-consent/PhxCovidScreeningConsent'),
        'PhxCovidScreeningDetailsConsent': () => import('@/components/metadata/inputs/covid-screening-details-consent/PhxCovidScreeningDetailsConsent'),
        'SpecificService': () => import('@/components/metadata/inputs/ServicesDropdown'),
        'OtherService': () => import('@/components/metadata/inputs/ServicesDropdown'),
        'Region': () => import('@/components/metadata/inputs/region/Region'),
        'CovidScreeningHealthcareWorker': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningHealthcareWorker'),
        'CovidScreeningHealthcareWorkerType': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningHealthcareWorkerType'),
        'CovidScreeningHealthcareInstitution': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningHealthcareInstitution'),
        'CovidScreeningHealthcareInstitutionType': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningHealthcareInstitutionType'),
        'CovidScreeningHealthcareSharingResult': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningHealthcareSharingResult'),
        'CovidScreeningConsentSharingInformations': () => import('@/components/metadata/inputs/checkbox/MetadataCheckbox.vue'),
        'CovidScreeningConsentReceiveResultByEmailSms': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningConsentReceiveResultByEmailSms'),
        'CovidScreeningPreferredCommunication': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningPreferredCommunication'),
        'CovidScreeningTravelledOutsideQuebec': () => import('@/components/metadata/inputs/checkbox/MetadataCheckbox.vue'),
        'CovidScreeningCountry': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningCountry'),
        'CovidScreeningCategory': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningCategory'),
        'CovidScreeningStudentSchoolRegion': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningStudentSchoolRegion'),
        'CovidScreeningStudentLevel': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningStudentLevel'),
        'CovidScreeningStudentInstitution': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningStudentInstitution'),
        'CovidScreeningWorkingQuebec': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningWorkingQuebec'),
        'CovidScreeningEmployerName': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningEmployerName'),
        'CovidScreeningChildNameOfNursery': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningChildNameOfNursery'),
        'CovidScreeningOutbreakNumber': () => import('@/components/metadata/inputs/covid-screening/CovidScreeningOutbreakNumber'),
        'IsOtherServiceWanted': () => import('@/components/metadata/inputs/is-other-service-wanted/IsOtherServiceWanted'),
        'HaveYouBeenToHospitalInTheLastThreeMonths': () => import('@/components/metadata/inputs/sampling/have-you-been-to-hospital-in-the-last-three-months/HaveYouBeenToHospitalInTheLastThreeMonths'),
        'CivilStatus': () => import('@/components/metadata/inputs/sampling/civil-status/CivilStatus'),
        'SpouseFirstName': () => import('@/components/metadata/inputs/sampling/spouse/SpouseFirstName'),
        'SpouseLastName': () => import('@/components/metadata/inputs/sampling/spouse/SpouseLastName'),
        'EmergencyContactFirstName': () => import('@/components/metadata/inputs/sampling/emergency-contact/EmergencyContactFirstName'),
        'EmergencyContactLastName': () => import('@/components/metadata/inputs/sampling/emergency-contact/EmergencyContactLastName'),
        'EmergencyContactRelationship': () => import('@/components/metadata/inputs/sampling/emergency-contact/EmergencyContactRelationship'),
        'EmergencyContactPhoneNumber': () => import('@/components/metadata/inputs/sampling/emergency-contact/EmergencyContactPhoneNumber'),
        'City': () => import('@/components/metadata/inputs/city/City'),
        'As624HealthProblemsQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'As624ImmunosuppressionQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'As624AllergicReactionsQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'As624BleedingDisorderQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'As624HasBloodTransfusionQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'As624PregnancyQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'As624LessThanSixMonthsMotherTreatmentQuestion': () => import('@/components/metadata/inputs/YesNoNaQuestion'),
        'ColorectalCancerScreeningConsentSharingInformations': () => import('@/components/metadata/inputs/checkbox/MetadataCheckbox.vue'),
        'ColorectalCancerScreeningConsentContactPhone': () => import('@/components/metadata/inputs/checkbox/MetadataCheckbox.vue'),
        'PqdcsEligibilityDate': () => import('@/components/metadata/inputs/pqdcs/PqdcsEligibilityDate'),
        'PqdcsPrescriptionDate': () => import('@/components/metadata/inputs/pqdcs/PqdcsPrescriptionDate'),
        'LegalFirstMiddleLastName': () => import('@/components/metadata/inputs/nsha/LegalFirstMiddleLastName'),
        'OfferLetterSentDate': () => import('@/components/metadata/inputs/nsha/OfferLetterSentDate'),
        'PlannedStartDate': () => import('@/components/metadata/inputs/nsha/PlannedStartDate'),
        'WorkedForNsha': () => import('@/components/metadata/inputs/nsha/WorkedForNsha'),
        'NameUsedPreviously': () => import('@/components/metadata/inputs/nsha/NameUsedPreviously'),
        'CoadminInjection': () => import('@/components/metadata/inputs/coadmin-injection/CoadminInjection'),
    },
    data() {
        return {
            metadataComments: MetadataEnum.COMMENTS,
        };
    },
    computed: {
        ...mapGetters({
            locale: 'locale',
            getBaseMetadataById: 'services/getBaseMetadataById',
            getCopySelectedServiceMetadataValuesOverrideApplied: 'services/getCopySelectedServiceMetadataValuesOverrideApplied',
        }),
    },
    methods: {
        getMetaDataComponent(meta) {
            return upperFirst(camelCase(this.getBaseMetadataById(meta.metadata).name));
        },
        updateProfileMetadata(metadataValues) {
            switch (metadataValues.metadata) {
                case MetadataEnum.NAM:
                    metadataValues.value = maskNam(metadataValues.value);
                    break;
                case MetadataEnum.CELLPHONE:
                case MetadataEnum.PHONE:
                    metadataValues.value = maskPhoneNumber(metadataValues.value);
                    break;
            }
            this.$emit('updateMetadata', metadataValues, true);
        },
        updateMetadataValue(meta, value) {
            const metadataObj = { ...meta, value };
            const metadata = this.getCopySelectedServiceMetadataValuesOverrideApplied;

            switch (meta.metadata) {
                case MetadataEnum.EMAIL:
                    this.$store.commit('metadataForm/SET_SEND_BY_EMAIL', !!metadataObj.value);
                    break;
                case MetadataEnum.CELLPHONE:
                    this.$store.commit('metadataForm/SET_SEND_BY_SMS', !!metadataObj.value);
                    break;
                /**
                 * Fix cases where an element has been set as required in ADMIN but doesn't make any logical sense.
                 * (e.g.: set "Required" for the unknown mother toggle)
                 * See this issue : https://gitlab.com/groups/trimoz/development/teams/-/epics/773
                 */
                case MetadataEnum.MOTHER_LAST_NAME:
                case MetadataEnum.MOTHER_FIRST_NAME:
                    const metadataUnknowMother = metadata[MetadataEnum.UNKNOWN_MOTHER];
                    if(isNil(metadataUnknowMother)) {
                        break;
                    }
                    const unknownMother = !!metadataUnknowMother?.value;
                    this.$emit('updateMetadata', { ...metadataUnknowMother, value: unknownMother });
                    break;
                case MetadataEnum.FATHER_LAST_NAME:
                case MetadataEnum.FATHER_FIRST_NAME:
                    const metadataUnknowFather = metadata[MetadataEnum.UNKNOWN_FATHER];
                    if(isNil(metadataUnknowFather)) {
                        break;
                    }
                    const unknownFather = !!metadataUnknowFather?.value;
                    this.$emit('updateMetadata', { ...metadataUnknowFather, value: unknownFather });
                    break;
            }
            this.$emit('updateMetadata', metadataObj);
        },
        updateOtherMetadata(event) {
            this.updateMetadataValue(event.metadata, event.value);
        },
        getComponentDependency(meta) {
            const metadata = this.getCopySelectedServiceMetadataValuesOverrideApplied;
            let showComponent = true;
            let unknownFather = null;
            let unknownMother = null;
            let covidScreeningHealthcareWorker = null;
            let covidScreeningHealthcareWorkerType = null;
            let covidScreeningHealthcareInstitution = null;
            let covidScreeningCategory = null;
            let samplingHaveYouBeenToHospitalInTheLastThreeMonths = null;

            switch (meta.metadata) {
                case MetadataEnum.FATHER_LAST_NAME:
                    unknownFather = !!metadata[MetadataEnum.UNKNOWN_FATHER]?.value;
                    showComponent = unknownFather === metadata[MetadataEnum.FATHER_LAST_NAME]?.requiredOnDependentMetadataValue;
                    break;
                case MetadataEnum.FATHER_FIRST_NAME:
                    unknownFather = !!metadata[MetadataEnum.UNKNOWN_FATHER]?.value;
                    showComponent = unknownFather === metadata[MetadataEnum.FATHER_FIRST_NAME]?.requiredOnDependentMetadataValue;
                    break;
                case MetadataEnum.MOTHER_LAST_NAME:
                    unknownMother = !!metadata[MetadataEnum.UNKNOWN_MOTHER]?.value;
                    showComponent = unknownMother === metadata[MetadataEnum.MOTHER_LAST_NAME]?.requiredOnDependentMetadataValue;
                    break;
                case MetadataEnum.MOTHER_FIRST_NAME:
                    unknownMother = !!metadata[MetadataEnum.UNKNOWN_MOTHER]?.value;
                    showComponent = unknownMother === metadata[MetadataEnum.MOTHER_FIRST_NAME]?.requiredOnDependentMetadataValue;
                    break;
                case MetadataEnum.BIRTH_PREMATURE_WEEK_COUNT:
                    const premature = metadata[MetadataEnum.BIRTH_PREMATURE]?.value;
                    showComponent = isNil(premature) ? true : premature;
                    break;
                case MetadataEnum.REQUESTER_NAME:
                    showComponent = metadata[MetadataEnum.REQUESTER_LINK]?.value;
                    break;
                case MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER_TYPE:
                    if (this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER])) {
                        covidScreeningHealthcareWorker = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER].value;
                        showComponent = isNull(covidScreeningHealthcareWorker) ? false : covidScreeningHealthcareWorker;
                    }
                    break;
                case MetadataEnum.COVID_SCREENING_HEALTHCARE_INSTITUTION:
                    if (
                        this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER]) &&
                        this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER_TYPE])
                    ) {
                        covidScreeningHealthcareWorkerType = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER_TYPE].value;
                        covidScreeningHealthcareWorker = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER].value;
                        if (covidScreeningHealthcareWorker && covidScreeningHealthcareWorkerType) {
                            showComponent = true;
                        }
                        else {
                            showComponent = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_INSTITUTION]?.requiredOnDependentMetadataValue;
                        }
                    }
                    break;
                case MetadataEnum.COVID_SCREENING_HEALTHCARE_INSTITUTION_TYPE:
                    if (
                        this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER_TYPE]) &&
                        this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER]) &&
                        this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_INSTITUTION])
                    ) {
                        covidScreeningHealthcareWorkerType = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER_TYPE].value;
                        covidScreeningHealthcareWorker = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER].value;
                        covidScreeningHealthcareInstitution = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_INSTITUTION].value;
                        if (!covidScreeningHealthcareWorker || !covidScreeningHealthcareWorkerType || !covidScreeningHealthcareInstitution) {
                            showComponent = false;
                        }
                        else if (covidScreeningHealthcareInstitution === 'Autre' || covidScreeningHealthcareInstitution === 'Other') {
                            showComponent = true;
                        }
                        else {
                            showComponent = covidScreeningHealthcareInstitution.requiredOnDependentMetadataValue;
                        }
                    }
                    break;
                case MetadataEnum.COVID_SCREENING_HEALTHCARE_SHARING_RESULT:
                    if (
                        this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER_TYPE]) &&
                        this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER]) &&
                        this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_INSTITUTION])
                    ) {
                        covidScreeningHealthcareWorkerType = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER_TYPE].value;
                        covidScreeningHealthcareWorker = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_WORKER].value;
                        covidScreeningHealthcareInstitution = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_INSTITUTION].value;
                        if (covidScreeningHealthcareWorker && covidScreeningHealthcareWorkerType && covidScreeningHealthcareInstitution) {
                            showComponent = true;
                        }
                        else {
                            showComponent = metadata[MetadataEnum.COVID_SCREENING_HEALTHCARE_SHARING_RESULT]?.requiredOnDependentMetadataValue;
                        }
                    }
                    break;
                case MetadataEnum.COVID_SCREENING_STUDENT_SCHOOL_REGION:
                case MetadataEnum.COVID_SCREENING_STUDENT_LEVEL:
                case MetadataEnum.COVID_SCREENING_STUDENT_INSTITUTION:
                    if (this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_CATEGORY])) {
                        covidScreeningCategory = metadata[MetadataEnum.COVID_SCREENING_CATEGORY].value;
                        showComponent = covidScreeningCategory === 'Étudiant' || covidScreeningCategory === 'Student';
                    }
                    break;
                case MetadataEnum.COVID_SCREENING_WORKING_QUEBEC:
                case MetadataEnum.COVID_SCREENING_EMPLOYER_NAME:
                    if (this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_CATEGORY])) {
                        covidScreeningCategory = metadata[MetadataEnum.COVID_SCREENING_CATEGORY].value;
                        showComponent = covidScreeningCategory === 'Travailleur' || covidScreeningCategory === 'Worker';
                    }
                    break;
                case MetadataEnum.COVID_SCREENING_CHILD_NAME_OF_NURSERY:
                    if (this.getDependencyMetadataStatus(metadata[MetadataEnum.COVID_SCREENING_CATEGORY])) {
                        covidScreeningCategory = metadata[MetadataEnum.COVID_SCREENING_CATEGORY].value;
                        showComponent = covidScreeningCategory === 'Enfant (0-5 ans)' || covidScreeningCategory === 'Child';
                    }
                    break;
                case MetadataEnum.CIVIL_STATUS:
                case MetadataEnum.SPOUSE_FIRST_NAME:
                case MetadataEnum.SPOUSE_LAST_NAME:
                case MetadataEnum.EMERGENCY_CONTACT_FIRST_NAME:
                case MetadataEnum.EMERGENCY_CONTACT_LAST_NAME:
                case MetadataEnum.EMERGENCY_CONTACT_RELATIONSHIP:
                case MetadataEnum.EMERGENCY_CONTACT_PHONE_NUMBER:
                    if (this.getDependencyMetadataStatus(metadata[MetadataEnum.HAVE_YOU_BEEN_TO_HOSPITAL_IN_THE_LAST_THREE_MONTHS])) {
                        showComponent = false;
                        samplingHaveYouBeenToHospitalInTheLastThreeMonths = metadata[MetadataEnum.HAVE_YOU_BEEN_TO_HOSPITAL_IN_THE_LAST_THREE_MONTHS].value;
                        if(samplingHaveYouBeenToHospitalInTheLastThreeMonths === 0) {
                            this.setSamplingMetadataRequiredOrNot(metadata, true);
                            showComponent = true;
                        }
                        else if (samplingHaveYouBeenToHospitalInTheLastThreeMonths === 1) {
                            this.setSamplingMetadataRequiredOrNot(metadata, false);
                            showComponent = false;
                        }
                    }
                    break;
                case MetadataEnum.PQDCS_ELIGIBILITY_DATE:
                    if (metadata[MetadataEnum.PQDCS_PRESCRIPTION_DATE]?.value) {
                        showComponent = false;
                    }
                    break;
                case MetadataEnum.PQDCS_PRESCRIPTION_DATE:
                    if (metadata[MetadataEnum.PQDCS_ELIGIBILITY_DATE]?.value) {
                        showComponent = false;
                    }
                    break;
            }
            return showComponent;
        },

        /**
         * @description Return string placeholder for metadata depending on current locale
         * @param {string} meta
         * @returns {string}
         */
        getPlaceholderFromMeta(meta) {
            const name = this.getBaseMetadataById(meta.metadata)['name'];
            return this.$i18n.messages[this.locale]
                ? this.$i18n.messages[this.locale].appt.placeholder[camelCase(name)]
                : name;
        },

        updatePatientUuid(uuid) {
            this.$emit('updatePatientUuid', uuid);
        },

        getDependencyMetadataStatus(meta) {
            return meta && meta.status === 1;
        },

        setSamplingMetadataRequiredOrNot(metadata, requiredValue) {
            metadata[MetadataEnum.CIVIL_STATUS].required = requiredValue;
            metadata[MetadataEnum.SPOUSE_FIRST_NAME].required = requiredValue;
            metadata[MetadataEnum.SPOUSE_LAST_NAME].required = requiredValue;
            metadata[MetadataEnum.EMERGENCY_CONTACT_FIRST_NAME].required = requiredValue;
            metadata[MetadataEnum.EMERGENCY_CONTACT_LAST_NAME].required = requiredValue;
            metadata[MetadataEnum.EMERGENCY_CONTACT_RELATIONSHIP].required = requiredValue;
            metadata[MetadataEnum.EMERGENCY_CONTACT_PHONE_NUMBER].required = requiredValue;
        },
    },
};
</script>
<style lang="scss">
    #metadata-form{
      .v-input--is-focused{
        label.v-label.v-label--active {
          color: rgba(#000, .54)!important;
          font-size: 21px;
          padding: 0 4px;
          background-color: #fff!important;
        }
      }
      .v-messages__message {
        font-size: 14px;
      }
      .v-counter {
        font-size: 14px;
      }
    }
</style>
